import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface BookingFormExperience {
  id: number;
  user_id?: number;
  name: string;
  /** Translated public label; name always remains the original booking value. */
  displayName?: string;
  description?: string | null;
  enabled: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

export interface BookingFormExperiencesState {
  experiences_enabled: boolean;
  /** V135 guest-freetext opt-in; absent on older backends → treated as false. */
  experiences_allow_custom?: boolean;
  data: BookingFormExperience[];
}

/**
 * CRUD client for the booking form experiences / set-menu products
 * (Back_End_Server /api/v1/booking-form-experiences).
 *
 * Management calls are JWT-authed — the AuthInterceptor attaches the token
 * for every environment.baseUrl request. The tenant always comes from the
 * token; the toggle lives on the shared restaurant_online_booking_form_settings
 * row and only ever writes its own column.
 */
@Injectable({ providedIn: 'root' })
export class BookingFormExperiencesService {
  private readonly apiUrl = `${environment.baseUrl}/api/v1/booking-form-experiences`;

  constructor(private http: HttpClient) {}

  /** Toggle state plus all experiences of the logged-in tenant, disabled ones included. */
  getExperiences(): Observable<BookingFormExperiencesState> {
    return this.http
      .get<{
        message: string;
        experiences_enabled: boolean;
        experiences_allow_custom?: boolean;
        data: BookingFormExperience[];
      }>(this.apiUrl)
      .pipe(
        map((res) => ({
          experiences_enabled: res.experiences_enabled === true,
          experiences_allow_custom: res.experiences_allow_custom === true,
          data: res.data ?? [],
        }))
      );
  }

  /**
   * Toggle plus enabled experiences for a restaurant's booking form — public,
   * no token, name-only rows. Fetch original names for booking values, then
   * join translated labels by ID when a page language is requested.
   */
  getPublicExperiences(
    userId: number,
    lang?: string
  ): Observable<BookingFormExperiencesState> {
    return this.http
      .get<{
        message: string;
        experiences_enabled: boolean;
        experiences_allow_custom?: boolean;
        data: BookingFormExperience[];
      }>(`${this.apiUrl}/public/${userId}`)
      .pipe(
        map((res) => ({
          experiences_enabled: res.experiences_enabled === true,
          experiences_allow_custom: res.experiences_allow_custom === true,
          data: res.data ?? [],
        })),
        switchMap((state) => {
          if (!lang || !state.experiences_enabled || !state.data.length) {
            return of(state);
          }
          return this.http
            .get<{ data: BookingFormExperience[] }>(
              `${this.apiUrl}/public/${userId}`,
              { params: { lang } }
            )
            .pipe(
              map((res) => {
                const labels = new Map((res.data ?? []).map((item) => [item.id, item.name]));
                return {
                  ...state,
                  data: state.data.map((item) => ({
                    ...item,
                    displayName: labels.get(item.id) || item.name,
                  })),
                };
              }),
              catchError(() => of(state))
            );
        })
      );
  }

  /** Master switch: guests must pick an experience iff enabled and rows exist. */
  setExperiencesEnabled(enabled: boolean): Observable<boolean> {
    return this.http
      .put<{ message: string; data: { experiences_enabled: boolean } }>(
        `${this.apiUrl}/toggle`,
        { enabled }
      )
      .pipe(map((res) => res.data?.experiences_enabled === true));
  }

  /** V135 opt-in: guests may type a custom experience ("Other") on the form. */
  setExperiencesAllowCustom(enabled: boolean): Observable<boolean> {
    return this.http
      .put<{ message: string; data: { experiences_allow_custom: boolean } }>(
        `${this.apiUrl}/allow-custom`,
        { enabled }
      )
      .pipe(map((res) => res.data?.experiences_allow_custom === true));
  }

  /**
   * One-time copy of the tenant's Set Menü names into the experiences list
   * (server-side, names only, case-insensitive duplicates skipped). Returns
   * the created rows plus the imported/skipped counts.
   */
  importSetMenus(): Observable<{
    imported: number;
    skipped: number;
    data: BookingFormExperience[];
  }> {
    return this.http
      .post<{
        message: string;
        imported: number;
        skipped: number;
        data: BookingFormExperience[];
      }>(`${this.apiUrl}/import-set-menus`, {})
      .pipe(
        map((res) => ({
          imported: Number(res.imported) || 0,
          skipped: Number(res.skipped) || 0,
          data: res.data ?? [],
        }))
      );
  }

  createExperience(experience: {
    name: string;
    enabled?: boolean;
    sort_order?: number;
  }): Observable<BookingFormExperience> {
    return this.http
      .post<{ message: string; data: BookingFormExperience }>(this.apiUrl, experience)
      .pipe(map((res) => res.data));
  }

  updateExperience(
    id: number,
    fields: Partial<Pick<BookingFormExperience, 'name' | 'enabled' | 'sort_order'>>
  ): Observable<BookingFormExperience> {
    return this.http
      .patch<{ message: string; data: BookingFormExperience }>(
        `${this.apiUrl}/${id}`,
        fields
      )
      .pipe(map((res) => res.data));
  }

  deleteExperience(id: number): Observable<void> {
    return this.http
      .delete<{ message: string }>(`${this.apiUrl}/${id}`)
      .pipe(map(() => undefined));
  }
}
